/**
 * The parts of an email's `<head>` a variant card needs, resolved for the
 * viewport being previewed.
 *
 * A card renders one element in a bare document, so it has no head of its own:
 * fonts fall back to a system face, and any rule the email kept in a stylesheet
 * (rather than inline) is gone. Computed styles cover what the ROOT element
 * inherits, but not what its descendants get from a class — that has to come
 * from the rules themselves.
 *
 * `@media` blocks are RESOLVED, not stripped. A card's document is a couple of
 * hundred pixels wide, so evaluating them at the card's own width would fire the
 * email's mobile rules inside every card (`.ht-mobile-hide { display: none }`).
 * Instead each block's width condition is evaluated against the viewport the
 * email is being previewed at, and matching blocks are unwrapped to top level —
 * so a card shows mobile styling in mobile view and desktop styling in desktop
 * view, which is what the email does.
 *
 * Known gaps, all of which degrade to "plainer than the email" rather than
 * broken: selectors anchored ABOVE the element (`.container > h1`) can't match in
 * a document that doesn't have those ancestors, sibling-index selectors
 * (`:first-child`) see a different tree, and a non-font stylesheet `<link>` is
 * skipped because its `@media` blocks can't be resolved without fetching it.
 */

import {
  findBlockEnd,
  mediaConditionAppliesAtWidth,
  stripCssComments,
} from "src/pages/lifecycle-studio/emails/email-editor/device-groups";

/** Hosts and paths that serve font CSS. Font stylesheets are `@font-face` and
 * nothing else, so they're safe to link as-is; an arbitrary template stylesheet
 * would bring `@media` rules we can't resolve. A miss here just means the old
 * fallback-face rendering. */
const FONT_STYLESHEET_PATTERN =
  /fonts\.googleapis\.com|fonts\.gstatic\.com|use\.typekit\.net|fonts\.bunny\.net|api\.fontshare\.com|\/fonts?\//i;

type MediaBlockSpan = {
  /** The `@media` prelude text, trimmed. */
  condition: string;
  /** Index of the `@media` keyword. */
  start: number;
  /** Body text between the block's braces (nested blocks included). */
  body: string;
  /** Index just past the block's closing brace. */
  end: number;
};

/**
 * The top-level `@media` blocks of COMMENT-STRIPPED CSS, in order — the one
 * scan under `resolveMediaBlocks` and `collectMediaConditions`, so every
 * consumer agrees on what counts as a media block. `tailIndex` is where clean
 * text resumes after the last block, or -1 when the scan hit a truncated
 * at-rule (an `@media` with no block, or an unbalanced one) and nothing
 * usable follows.
 */
function scanMediaBlocks(text: string): {
  blocks: MediaBlockSpan[];
  tailIndex: number;
} {
  const lowered = text.toLowerCase();
  const blocks: MediaBlockSpan[] = [];
  let index = 0;
  while (index < text.length) {
    const start = lowered.indexOf("@media", index);
    if (start === -1) break;
    const blockStart = text.indexOf("{", start);
    if (blockStart === -1) return { blocks, tailIndex: -1 };
    const end = findBlockEnd(text, blockStart);
    if (end === -1) return { blocks, tailIndex: -1 };
    blocks.push({
      condition: text.slice(start + "@media".length, blockStart).trim(),
      start,
      body: text.slice(blockStart + 1, end),
      end: end + 1,
    });
    index = end + 1;
  }
  return { blocks, tailIndex: index };
}

/**
 * Unwrap `@media` blocks that apply at `viewportWidth`, drop the rest.
 *
 * The condition evaluation and the brace matching are the editor's existing
 * ones, shared with the device-group annotation pass — that pass resolves the
 * same emails' `@media` rules at its own eval widths, so the two agree on which
 * blocks are live at a width by construction.
 */
export function resolveMediaBlocks(css: string, viewportWidth: number): string {
  // Comments can hold braces or whole commented-out rules, either of which
  // would derail the brace matching below.
  const text = stripCssComments(css);
  const { blocks, tailIndex } = scanMediaBlocks(text);
  let result = "";
  let cursor = 0;
  for (const block of blocks) {
    result += text.slice(cursor, block.start);
    if (mediaConditionAppliesAtWidth(block.condition, viewportWidth)) {
      // Recurse: a nested block has to be evaluated too, not hoisted to where
      // the card's own width would decide it.
      result += resolveMediaBlocks(block.body, viewportWidth);
    }
    cursor = block.end;
  }
  if (tailIndex !== -1) {
    result += text.slice(tailIndex);
  }
  return result;
}

/**
 * Every `@media` condition in the CSS, nested blocks included, in document
 * order — for consumers that read the conditions themselves rather than
 * resolving at one width (see `computeDesktopPreviewWidth`).
 */
export function collectMediaConditions(css: string): string[] {
  const out: string[] = [];
  const visit = (text: string): void => {
    for (const block of scanMediaBlocks(text).blocks) {
      out.push(block.condition);
      visit(block.body);
    }
  };
  visit(stripCssComments(css));
  return out;
}

/**
 * The email's font stylesheet `<link>`s, for a branch document's head.
 *
 * Split from the CSS below because the two have different lifetimes: a link is
 * viewport-independent, so it can live in the document the frame freezes at
 * mount, while the CSS has to be re-resolved and re-injected whenever the
 * previewed viewport changes.
 */
export function collectFontLinks(head: HTMLHeadElement | null): string {
  if (!head) return "";
  return [...head.querySelectorAll("link")]
    .filter(
      (link) =>
        (link.getAttribute("rel") ?? "").toLowerCase() === "stylesheet" &&
        FONT_STYLESHEET_PATTERN.test(link.getAttribute("href") ?? ""),
    )
    .map((link) => link.outerHTML)
    .join("\n");
}

/** `collectFontLinks` from a raw email string. */
export function extractPreviewFontLinks(emailHtml: string): string {
  return collectFontLinks(
    new DOMParser().parseFromString(emailHtml, "text/html").head,
  );
}

/**
 * The raw text of every `<style>` in the email's head, `@media` UNRESOLVED.
 *
 * Split from resolution because the two have different lifetimes: the sheets
 * change only with the email, while resolution has to re-run whenever the
 * previewed viewport changes. Reading them once and resolving many times is
 * what keeps a device toggle from re-parsing the whole email.
 */
export function collectStyleSheets(head: HTMLHeadElement | null): string[] {
  if (!head) return [];
  return [...head.querySelectorAll("style")].map(
    (style) => style.textContent ?? "",
  );
}

/** Resolve collected sheets for a viewport — see `extractPreviewCss`. */
export function resolveEmailCss(
  styleSheets: string[],
  viewportWidth: number,
): string {
  return styleSheets
    .map((css) => resolveMediaBlocks(css, viewportWidth))
    .join("\n")
    .trim();
}

/**
 * The email's inline CSS with `@media` resolved for `viewportWidth` — injected
 * into the live document rather than baked in, so a device switch re-resolves it
 * instead of leaving cards on the previous viewport's rules.
 *
 * Every `<style>` is email content: the editor's own injected sheets
 * (`ht-editable-style`, `ht-canonical-style`) exist only in a live document and
 * are stripped before serialization, so saved HTML never carries them.
 */
export function extractPreviewCss(
  emailHtml: string,
  viewportWidth: number,
): string {
  const head = new DOMParser().parseFromString(emailHtml, "text/html").head;
  return resolveEmailCss(collectStyleSheets(head), viewportWidth);
}
